// Réponses de l'API de la participation (M17, lot L5-C1). Les vues des agents sont des interfaces ; les
// vues du portail (représentant, usager, public) sont des schémas stricts construits champ par champ :
// résultats d'une consultation agrégés par option, sans liste ni identité des répondants (D-251).
import { z } from 'zod';
import type {
  CategorieBesoin,
  CibleOrientation,
  EtatBesoinQuartier,
  EtatConsultationQuartier,
  EtatEngagementQuartier,
  EtatReunion,
  OrigineBesoin,
  PrioriteBesoin,
  PublicConsultation,
  RegleReponse,
} from './participation';

export interface ResumeQuartier {
  id: string;
  nom: string;
  arrondissement: { id: string; nom: string } | null;
  a_emprise: boolean;
  representants_en_exercice: number;
  besoins_ouverts: number;
  engagements_en_cours: number;
  consultations_ouvertes: number;
}

export interface Representant {
  id: string;
  quartier_id: string;
  compte: { id: string; nom: string | null; identifiant: string };
  debut: string;
  fin: string;
  mandat: string | null;
  motif_fin: string | null;
  en_exercice: boolean;
  fictif: boolean;
}

export interface ReunionQuartier {
  id: string;
  quartier_id: string;
  date: string;
  lieu: string;
  objet: string;
  etat: EtatReunion;
  compte_rendu: string | null;
  participants: number | null;
  motif_annulation: string | null;
  fictif: boolean;
}

/** Objet vers lequel un besoin est orienté, lu dans son module d'origine si le lecteur peut le voir. */
export interface OrientationLue {
  cible: CibleOrientation;
  id: string;
  creee: boolean;
  visible: boolean;
  numero: string | null;
  libelle: string | null;
  etat: string | null;
  orientee_le: string;
}

export interface BesoinQuartier {
  id: string;
  numero: string;
  quartier: { id: string; nom: string };
  titre: string;
  description: string;
  categorie: CategorieBesoin;
  priorite: PrioriteBesoin;
  origine: OrigineBesoin;
  etat: EtatBesoinQuartier;
  position: [number, number] | null;
  adresse: string | null;
  reunion_id: string | null;
  exprime_par: string | null;
  observation: string | null;
  motif_rejet: string | null;
  orientation: OrientationLue | null;
  cree_le: string;
  qualifie_le: string | null;
  traite_le: string | null;
  fictif: boolean;
}

export interface ResultatOption {
  option: string;
  reponses: number;
}

export interface ConsultationQuartier {
  id: string;
  numero: string;
  quartier: { id: string; nom: string };
  question: string;
  options: string[];
  debut: string;
  fin: string;
  public_cible: PublicConsultation;
  regle_reponse: RegleReponse;
  etat: EtatConsultationQuartier;
  publiee_le: string | null;
  /** Résultats agrégés (agents habilités) : nombre de réponses par option, jamais les répondants. */
  resultats: ResultatOption[];
  total_reponses: number;
  fictif: boolean;
}

export interface EngagementQuartier {
  id: string;
  quartier_id: string;
  intitule: string;
  description: string | null;
  echeance: string;
  etat: EtatEngagementQuartier;
  en_retard: boolean;
  besoin: { id: string; numero: string } | null;
  reunion_id: string | null;
  service: { id: string; nom: string } | null;
  suivi: { le: string; etat: EtatEngagementQuartier; observation: string }[];
  fictif: boolean;
}

export interface FicheQuartier extends ResumeQuartier {
  representants: Representant[];
  reunions: ReunionQuartier[];
  besoins: BesoinQuartier[];
  consultations: ConsultationQuartier[];
  engagements: EngagementQuartier[];
}

// --- Vues du portail ------------------------------------------------------------------------------

/** Consultation publique : question, période et, après la clôture, résultats agrégés par option. */
export const schemaConsultationPublique = z
  .object({
    id: z.string().uuid(),
    numero: z.string(),
    quartier: z.string(),
    question: z.string(),
    options: z.array(z.string()),
    debut: z.string(),
    fin: z.string(),
    etat: z.enum(['a_venir', 'ouverte', 'close']),
    public_cible: z.enum(['usagers', 'representants']),
    regle_reponse: z.enum(['unique', 'modifiable']),
    resultats: z.array(z.object({ option: z.string(), reponses: z.number().int() }).strict()).nullable(),
    total_reponses: z.number().int().nullable(),
    fictif: z.boolean(),
  })
  .strict();
export type ConsultationPublique = z.infer<typeof schemaConsultationPublique>;

/** Consultation vue par un usager connecté : la vue publique et son propre choix. */
export const schemaConsultationUsager = schemaConsultationPublique
  .extend({ collectivite: z.object({ code: z.string(), nom: z.string() }).strict(), peut_repondre: z.boolean(), mon_choix: z.number().int().nullable() })
  .strict();
export type ConsultationUsager = z.infer<typeof schemaConsultationUsager>;

/** Désignation en cours d'un représentant (portail). */
export const schemaDesignationUsager = z
  .object({
    id: z.string().uuid(),
    collectivite: z.object({ code: z.string(), nom: z.string() }).strict(),
    quartier: z.object({ id: z.string().uuid(), nom: z.string() }).strict(),
    debut: z.string(),
    fin: z.string(),
  })
  .strict();
export type DesignationUsager = z.infer<typeof schemaDesignationUsager>;

/** Quartier vu par son représentant : besoins (sans auteur), réunions tenues, engagements, consultations ouvertes. */
export const schemaQuartierRepresentant = z
  .object({
    designation: schemaDesignationUsager,
    besoins: z.array(z.object({
      id: z.string().uuid(),
      numero: z.string(),
      titre: z.string(),
      categorie: z.string(),
      etat: z.string(),
      priorite: z.string(),
      cree_le: z.string(),
      orientation: z.object({ cible: z.enum(['signalement', 'projet_travaux']), numero: z.string().nullable(), etat: z.string().nullable() }).strict().nullable(),
      de_moi: z.boolean(),
    }).strict()),
    reunions: z.array(z.object({ id: z.string().uuid(), date: z.string(), lieu: z.string(), objet: z.string(), etat: z.string(), compte_rendu: z.string().nullable() }).strict()),
    engagements: z.array(z.object({ id: z.string().uuid(), intitule: z.string(), echeance: z.string(), etat: z.string(), en_retard: z.boolean() }).strict()),
    consultations: z.array(z.object({ id: z.string().uuid(), numero: z.string(), question: z.string(), etat: z.string(), fin: z.string() }).strict()),
  })
  .strict();
export type QuartierRepresentant = z.infer<typeof schemaQuartierRepresentant>;
